import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Video, Mic, Upload, CheckCircle, AlertTriangle, Loader2, X } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

export function RemoteCaptureMobilePage() {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState<'FOTO' | 'VIDEO' | 'AUDIO'>('FOTO');
  const [cameraConfig, setCameraConfig] = useState<'FRONTAL' | 'TRASEIRA'>('FRONTAL');
  const [requestStatus, setRequestStatus] = useState<string>('PENDENTE');

  // Media state
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [webcamActive, setWebcamActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);

  // Galler/file inputs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Status de Envio
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    fetchRequestInfo();
    return () => {
      stopCamera();
    };
  }, [id]);

  useEffect(() => {
    if (success) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            try {
              window.close();
            } catch (err) {
              console.warn('window.close() falhou:', err);
            }
            setIsClosed(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [success]);

  const fetchRequestInfo = async () => {
    if (!id) return;
    try {
      const res = await api.get(`/remote-capture/status/${id}`);
      setTipo(res.data.tipo || 'FOTO');
      setCameraConfig(res.data.camera || 'FRONTAL');
      setRequestStatus(res.data.status);
      if (res.data.status === 'CONCLUIDO') {
        setSuccess(true);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const startCamera = async (facing: 'user' | 'environment') => {
    stopCamera();
    
    // Validar se o contexto de mídia está disponível (HTTPS ou localhost)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Acesso à câmera/microfone indisponível. A captura remota exige uma conexão segura (HTTPS).');
      return;
    }

    // Verificar se a permissão foi negada anteriormente (se a API for suportada)
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionName = (tipo === 'AUDIO' ? 'microphone' : 'camera') as PermissionName;
        const permissionStatus = await navigator.permissions.query({ name: permissionName });
        if (permissionStatus.state === 'denied') {
          toast.error('O acesso à câmera/microfone foi bloqueado pelo navegador. Por favor, autorize nas configurações do seu site.');
          return;
        }
      }
    } catch (e) {
      // Query permissions não suportada em alguns navegadores (ex: Safari)
    }

    try {
      const constraints: MediaStreamConstraints = {
        video: tipo === 'FOTO' || tipo === 'VIDEO' ? { facingMode: facing } : false,
        audio: tipo === 'VIDEO' || tipo === 'AUDIO',
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      streamRef.current = mediaStream;
      setWebcamActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (e: any) {
      console.error('Erro ao acessar mídia:', e);
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        toast.error('Permissão de câmera/microfone negada. É necessário autorizar para realizar a captura.');
      } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
        toast.error('Nenhum dispositivo de câmera ou microfone foi encontrado no celular.');
      } else {
        toast.error('Erro ao acessar câmera ou microfone do celular. Verifique as permissões.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setStream(null);
    setWebcamActive(false);
    setRecording(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg');
      setMediaPreview(dataUrl);
      stopCamera();
    }
  };

  const startMediaRecording = () => {
    if (!stream) return;
    setRecordedChunks([]);

    let options: MediaRecorderOptions = {};
    if (tipo === 'AUDIO') {
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg',
        'audio/mp4',
        'audio/aac'
      ];
      const supported = types.find(t => MediaRecorder.isTypeSupported(t));
      if (supported) options = { mimeType: supported };
    } else {
      const types = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4'
      ];
      const supported = types.find(t => MediaRecorder.isTypeSupported(t));
      if (supported) options = { mimeType: supported };
    }

    try {
      const recorder = new MediaRecorder(stream, options);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
          setRecordedChunks((prev) => [...prev, e.data]);
        }
      };

      recorder.onstop = () => {
        const typeStr = tipo === 'AUDIO' ? (options.mimeType || 'audio/webm') : (options.mimeType || 'video/webm');
        const blob = new Blob(chunks, { type: typeStr });
        setMediaBlob(blob);
        const url = URL.createObjectURL(blob);
        setMediaPreview(url);
      };

      recorder.start(100);
      setMediaRecorder(recorder);
      setRecording(true);
    } catch (err: any) {
      console.error('Error starting MediaRecorder on mobile:', err);
      toast.error('Erro ao iniciar gravação: ' + err.message);
    }
  };

  const stopMediaRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setRecording(false);
      stopCamera();
    }
  };

  const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('O arquivo selecionado é muito grande (limite: 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setMediaPreview(reader.result as string);
      stopCamera();
    };
    reader.readAsDataURL(file);
  };

  const submitMedia = async () => {
    if (!mediaPreview) return;
    setSending(true);
    try {
      let base64Data = mediaPreview;

      // Se for Blob (video/audio gravado localmente)
      if (mediaBlob) {
        base64Data = await readAsDataURL(mediaBlob);
      }

      await api.post(`/remote-capture/upload/${id}`, { base64Data });
      setSuccess(true);
    } catch (e: any) {
      console.error('Upload error:', e);
      toast.error(e.response?.data?.message || 'Erro ao enviar arquivo para o servidor.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#020617', color: '#94a3b8' }}>
        <Loader2 className="animate-spin" size={32} style={{ color: '#3b82f6' }} />
        <span style={{ fontSize: '0.85rem', marginTop: 12 }}>Carregando solicitação...</span>
      </div>
    );
  }

  if (isClosed) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)',
        color: '#fff',
        padding: 24,
        textAlign: 'center',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div style={{ display: 'inline-flex', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '50%', padding: 20, border: '1px solid rgba(255, 255, 255, 0.1)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' }}>
          <X size={48} style={{ color: '#94a3b8' }} />
        </div>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: 24, color: '#f8fafc' }}>
          Sessão Encerrada
        </h2>
        <p style={{ fontSize: '0.88rem', color: '#94a3b8', marginTop: 10, maxWidth: 320, lineHeight: 1.6 }}>
          Esta sessão de captura foi encerrada. Você já pode fechar esta aba do navegador com segurança.
        </p>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            marginTop: 28,
            padding: '12px 24px',
            borderRadius: 8,
            border: 'none',
            background: '#1e293b',
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: 650,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#334155'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#1e293b'; }}
        >
          Ir para o Portal
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)',
        color: '#fff',
        padding: 24,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Círculos de pulso de sucesso */}
        <div style={{
          position: 'absolute', width: 220, height: 220, borderRadius: '50%',
          background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.1)',
          animation: 'pulse-ring 2s infinite ease-in-out', zIndex: 0, pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute', width: 340, height: 340, borderRadius: '50%',
          background: 'rgba(16, 185, 129, 0.02)', border: '1px solid rgba(16, 185, 129, 0.05)',
          animation: 'pulse-ring 3s infinite ease-in-out 0.5s', zIndex: 0, pointerEvents: 'none'
        }} />

        {/* Botão de Fechar "X" no topo direito */}
        <button
          onClick={() => {
            try {
              window.close();
            } catch {}
            setIsClosed(true);
          }}
          style={{
            position: 'absolute', top: 20, right: 20, zIndex: 10,
            background: 'rgba(255, 255, 255, 0.08)', border: 'none', borderRadius: '50%',
            color: '#fff', cursor: 'pointer', padding: 10, display: 'flex',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          <X size={18} />
        </button>

        {/* Card Conteúdo com animação de pop */}
        <div style={{ zIndex: 1, animation: 'success-pop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) both' }}>
          <div style={{ display: 'inline-flex', background: 'rgba(16, 185, 129, 0.15)', borderRadius: '50%', padding: 20, border: '2px solid #10b981', boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)' }}>
            <CheckCircle size={72} style={{ color: '#10b981' }} />
          </div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 900, marginTop: 24, letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #10b981, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Mídia Enviada!
          </h2>
          <p style={{ fontSize: '0.88rem', color: '#94a3b8', marginTop: 10, maxWidth: 320, lineHeight: 1.6 }}>
            O arquivo foi enviado com sucesso. Sua tela no computador já foi atualizada!
          </p>

          {/* Badge de Contagem Regressiva */}
          <div style={{
            marginTop: 32, display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', borderRadius: 999, background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.78rem', color: '#94a3b8'
          }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981', animation: 'pulse-dot 1s infinite alternate' }} />
            Fechando esta tela em <strong style={{ color: '#fff', fontSize: '0.85rem' }}>{countdown}s</strong>
          </div>
        </div>

        {/* Estilos adicionais de animação */}
        <style>{`
          @keyframes success-pop {
            0% { transform: scale(0.3) rotate(-15deg); opacity: 0; filter: blur(4px); }
            70% { transform: scale(1.1) rotate(5deg); }
            100% { transform: scale(1) rotate(0); opacity: 1; filter: none; }
          }
          @keyframes pulse-ring {
            0% { transform: scale(0.85); opacity: 0.5; }
            50% { transform: scale(1.05); opacity: 0.25; }
            100% { transform: scale(1.25); opacity: 0; }
          }
          @keyframes pulse-dot {
            from { transform: scale(0.8); opacity: 0.5; }
            to { transform: scale(1.2); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#020617', color: '#fff', padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #1e293b', paddingBottom: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            {tipo === 'FOTO' && <Camera size={18} style={{ color: '#3b82f6' }} />}
            {tipo === 'VIDEO' && <Video size={18} style={{ color: '#ef4444' }} />}
            {tipo === 'AUDIO' && <Mic size={18} style={{ color: '#10b981' }} />}
            Captura Remota
          </h1>
          <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>Solicitado por sua sessão no computador.</p>
        </div>
        <button
          onClick={() => {
            stopCamera();
            try {
              window.close();
            } catch {}
            setIsClosed(true);
          }}
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: 'none',
            borderRadius: '50%',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s, color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = '#94a3b8';
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Main Viewfinder / Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, justifyContent: 'center' }}>
        {!webcamActive && !mediaPreview && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            {tipo === 'FOTO' && <Camera size={64} style={{ color: '#3b82f6', opacity: 0.8 }} />}
            {tipo === 'VIDEO' && <Video size={64} style={{ color: '#ef4444', opacity: 0.8 }} />}
            {tipo === 'AUDIO' && <Mic size={64} style={{ color: '#10b981', opacity: 0.8 }} />}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
              <button 
                onClick={() => startCamera(cameraConfig === 'TRASEIRA' ? 'environment' : 'user')}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: '#3b82f6', color: '#fff',
                  fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                Abrir Câmera do Celular
              </button>
              
              <button 
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10, border: '1px solid #334155', background: 'transparent', color: '#fff',
                  fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                <Upload size={16} /> Escolher da Galeria
              </button>
            </div>
          </div>
        )}

        {webcamActive && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14 }}>
            <div style={{ flex: 1, background: '#000', borderRadius: 12, overflow: 'hidden', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {(tipo === 'FOTO' || tipo === 'VIDEO') ? (
                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <Mic size={48} className="animate-pulse" style={{ color: '#10b981' }} />
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Gravando áudio...</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, paddingBottom: 10 }}>
              {tipo === 'FOTO' && (
                <button 
                  onClick={capturePhoto}
                  style={{ width: '100%', padding: 14, borderRadius: 10, background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 700 }}
                >
                  Tirar Foto
                </button>
              )}

              {(tipo === 'VIDEO' || tipo === 'AUDIO') && (
                !recording ? (
                  <button 
                    onClick={startMediaRecording}
                    style={{ width: '100%', padding: 14, borderRadius: 10, background: '#ef4444', color: '#fff', border: 'none', fontWeight: 700 }}
                  >
                    Iniciar Gravação
                  </button>
                ) : (
                  <button 
                    onClick={stopMediaRecording}
                    style={{ width: '100%', padding: 14, borderRadius: 10, background: '#10b981', color: '#fff', border: 'none', fontWeight: 700 }}
                  >
                    Terminar Gravação
                  </button>
                )
              )}

              <button 
                onClick={stopCamera}
                style={{ padding: '0 20px', borderRadius: 10, border: '1px solid #334155', background: 'transparent', color: '#fff' }}
              >
                Voltar
              </button>
            </div>
          </div>
        )}

        {mediaPreview && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14 }}>
            <div style={{ flex: 1, background: '#0a0a0a', borderRadius: 12, overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {tipo === 'FOTO' && <img src={mediaPreview} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
              {tipo === 'VIDEO' && <video src={mediaPreview} controls style={{ width: '100%', height: '100%' }} />}
              {tipo === 'AUDIO' && <audio src={mediaPreview} controls style={{ width: '90%' }} />}
            </div>

            <div style={{ display: 'flex', gap: 12, paddingBottom: 10 }}>
              <button 
                onClick={submitMedia}
                disabled={sending}
                style={{ flex: 1, padding: 14, borderRadius: 10, background: '#10b981', color: '#fff', border: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {sending ? <Loader2 className="animate-spin" size={16} /> : 'Enviar p/ Computador'}
              </button>
              
              <button 
                onClick={() => {
                  setMediaPreview(null);
                  setMediaBlob(null);
                  if (tipo === 'FOTO' || tipo === 'VIDEO') {
                    startCamera(cameraConfig === 'TRASEIRA' ? 'environment' : 'user');
                  }
                }}
                disabled={sending}
                style={{ padding: '0 20px', borderRadius: 10, border: '1px solid #334155', background: 'transparent', color: '#fff' }}
              >
                Refazer
              </button>
            </div>
          </div>
        )}
      </div>

      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleGallerySelect} 
        style={{ display: 'none' }} 
        accept={tipo === 'FOTO' ? 'image/*' : tipo === 'VIDEO' ? 'video/*' : 'audio/*'} 
      />
    </div>
  );
}

// Helper to read Blob as Base64 Data URL
const readAsDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao ler arquivo de mídia.'));
    reader.readAsDataURL(blob);
  });
};
